import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, Home, MessageCircle, BookOpen, Brain, Moon, Users, Sparkles, Shield, Crown, Settings, Coins, Bell, LogOut, Activity, Heart, Building2, Lightbulb, Stethoscope, UserCircle, Waves, UtensilsCrossed, Target, Compass, Star, Plus, ClipboardList, Dumbbell, Wind, TreePine, Gem, Calendar, PenLine, CircleDot, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { useUserRole } from '@/hooks/useUserRole';
import { User } from '@supabase/supabase-js';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { MobileNavIntro } from './MobileNavIntro';
import { useTourController, getCurrentTourStep } from '@/hooks/useTourController';

interface MobileNavProps {
  user: User | null;
  onSignOut: () => void;
}

export const MobileNav = ({ user, onSignOut }: MobileNavProps) => {
  const [open, setOpen] = useState(false);
  const [favorites, setFavorites] = useState<string[]>([]);
  const [isTourRunning, setIsTourRunning] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { isAdmin, isPremium, isClinician, hasClinicianConnection } = useUserRole(user);
  const tourController = useTourController();

  // Listen for tour controller events to open/close menu
  useEffect(() => {
    const unsubscribe = tourController.subscribe((action) => {
      if (action === 'open-menu') {
        setOpen(true);
      } else if (action === 'close-menu') {
        setOpen(false);
      } else if (action === 'start-tour') {
        setIsTourRunning(true);
      } else if (action === 'end-tour') {
        setIsTourRunning(false);
      }
    });
    return unsubscribe;
  }, [tourController]);

  // Load favorites from localStorage
  useEffect(() => {
    const savedFavorites = localStorage.getItem('therappi-favorites');
    if (savedFavorites) {
      setFavorites(JSON.parse(savedFavorites));
    }
  }, []);

  const toggleFavorite = (path: string) => {
    const newFavorites = favorites.includes(path)
      ? favorites.filter(f => f !== path)
      : [...favorites, path];
    setFavorites(newFavorites);
    localStorage.setItem('therappi-favorites', JSON.stringify(newFavorites));
    toast.success(favorites.includes(path) ? 'Removed from favorites' : 'Added to favorites');
  };

  // All main navigation items for the app - grouped by 6 top-level domains
  const allNavLinks = [
    // HOME
    { path: '/dashboard', label: 'Dashboard', icon: Home, category: 'home' },
    { path: '/daily-check-in', label: 'Daily Check-In', icon: ClipboardList, category: 'home' },
    // THERAPPI AI
    { path: '/therapy', label: 'Therappi® AI', icon: MessageCircle, category: 'therappi-ai', hint: 'Choose your preferred therapy approach (QSP, CBT etc).' },
    // TRACK
    { path: '/nutrition', label: 'Food & Nutrition', icon: UtensilsCrossed, category: 'track' },
    { path: '/sleep-and-dreams', label: 'Sleep & Dreams', icon: Moon, category: 'track', hint: 'Dream journal + Dream Analysis bot live here.' },
    { path: '/exercise', label: 'Exercise Log', icon: Dumbbell, category: 'track' },
    { path: '/goals', label: 'Goals & Habits', icon: Target, category: 'track' },
    { path: '/lunar-cycle', label: 'Lunar & Cycle Tracking', icon: CircleDot, category: 'track' },
    { path: '/whoop-dashboard', label: 'Therappi® x WHOOP®', icon: Activity, category: 'track' },
    { path: '/journal', label: 'Journal', icon: PenLine, category: 'track' },
    // SANCTUARY
    { path: '/sanctuary', label: 'The Sanctuary', icon: TreePine, category: 'sanctuary' },
    { path: '/meditation-library', label: 'Meditation Medication®', icon: Brain, category: 'sanctuary' },
    { path: '/breathwork', label: 'Breathwork', icon: Wind, category: 'sanctuary' },
    { path: '/rock-remedies', label: 'Rock Remedies™', icon: Gem, category: 'sanctuary' },
    { path: '/rituals', label: 'Daily Healthy Habits', icon: Calendar, category: 'sanctuary' },
    // GROW
    { path: '/telempathy-tools', label: 'Telempathy Teachings®', icon: Waves, category: 'grow', hint: 'Soul Guidance lives here - for intuitive guidance and symbolic interpretation (separate from reflection chat).' },
    { path: '/manifestation', label: 'Manifestation', icon: Sparkles, category: 'grow' },
    { path: '/future-self-navigator', label: 'Future-Self Spotlight', icon: Compass, category: 'grow' },
    { path: '/course-library', label: 'Courses', icon: BookOpen, category: 'grow' },
    // CONNECT
    { path: '/connect-and-grow', label: 'Connect & Grow', icon: Users, category: 'connect' },
    { path: '/connect-and-grow#therapist', label: 'Connect with Therapist', icon: Stethoscope, category: 'connect' },
    { path: '/relational', label: 'Relational Hub', icon: Heart, category: 'connect' },
    // ACCOUNT & SUPPORT
    { path: '/insights', label: 'AI Insights', icon: Lightbulb, category: 'account' },
    { path: '/credits', label: 'Credits & AI Usage', icon: Coins, category: 'account' },
    { path: '/crisis', label: 'Crisis Support', icon: Shield, category: 'support' },
  ];

  // Get favourite links
  const favoriteLinks = allNavLinks.filter(link => favorites.includes(link.path));
  
  // Tour step 4 demo favorites: show Therappi AI and The Sanctuary with gold stars
  const tourDemoFavorites = [
    allNavLinks.find(l => l.path === '/therapy')!,
    allNavLinks.find(l => l.path === '/sanctuary')!,
  ].filter(Boolean);
  
  // Default favorites if none set (when not in tour)
  const defaultFavorites = [
    allNavLinks.find(l => l.path === '/dashboard')!,
    allNavLinks.find(l => l.path === '/therapy')!,
    allNavLinks.find(l => l.path === '/daily-check-in')!,
  ].filter(Boolean);
  
  // During tour step 4, show demo favorites; otherwise show user's favorites or defaults
  const currentStep = getCurrentTourStep();
  const isStep4Tour = currentStep === 3;
  const displayFavorites = isStep4Tour 
    ? tourDemoFavorites 
    : (favoriteLinks.length > 0 ? favoriteLinks : defaultFavorites);
  
  // Debug: Log current tour state
  console.log('[MobileNav] Tour state:', { isTourRunning, currentStep, isStep4Tour, displayFavoritesCount: displayFavorites.length, favPaths: displayFavorites.map(f => f.path) });

  const isActive = (path: string) => location.pathname === path;

  const renderNavLink = (link: typeof allNavLinks[0], bgClass: string) => {
    const Icon = link.icon;
    const active = isActive(link.path);
    
    // Add data-tour attribute for specific items the tour highlights
    const dataTourMap: Record<string, string> = {
      '/therapy': 'therappi-ai-link',
      '/daily-check-in': 'checkin-link',
      '/nutrition': 'nutrition-link',
      '/sanctuary': 'sanctuary-link',
      '/whoop-dashboard': 'whoop-link',
      '/connect-and-grow': 'community-link',
      '/connect-and-grow#therapist': 'clinician-link',
      '/relational': 'relational-link',
    };
    const dataTourAttr = dataTourMap[link.path];
    
    return (
      <div key={link.path} className="relative group">
        <Link
          to={link.path}
          onClick={(e) => {
            // Prevent navigation during tour to avoid disrupting the tour flow
            if (isTourRunning) {
              e.preventDefault();
              return;
            }
            setOpen(false);
          }}
          data-tour={dataTourAttr}
          className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${
            active
              ? 'bg-primary text-primary-foreground'
              : bgClass
          }`}
        >
          <Icon className="h-5 w-5" />
          <div className="flex-1 min-w-0">
            <span className="font-medium block" dangerouslySetInnerHTML={{ __html: link.label.replace(/®/g, '<sup class="text-[0.5em] align-super">®</sup>').replace(/™/g, '<sup class="text-[0.5em] align-super">™</sup>') }} />
            {link.hint && (
              <span className={`text-[11px] leading-tight block mt-0.5 ${active ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                {link.hint}
              </span>
            )}
          </div>
        </Link>
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            toggleFavorite(link.path);
          }}
          // During step 4, set data-tour on Therappi AI star (first demo favourite)
          data-tour={getCurrentTourStep() === 3 && link.path === '/therapy' ? 'favourite-star-demo' : undefined}
          data-testid={getCurrentTourStep() === 3 && link.path === '/therapy' ? 'favourite-star-demo' : undefined}
          className={`absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full transition-all pointer-events-auto ${
            favorites.includes(link.path) 
              ? 'text-secondary opacity-100' 
              : (getCurrentTourStep() === 3 && (link.path === '/therapy' || link.path === '/sanctuary'))
                ? 'opacity-100' // Tour step 4: gold stars on demo favorites
                : 'text-foreground/40 opacity-100 hover:text-yellow-500'
          }`}
          style={{ zIndex: 260 }}
          title={favorites.includes(link.path) ? 'Remove from favorites' : 'Add to favorites'}
        >
          <Star 
            className={`h-4 w-4 ${favorites.includes(link.path) || (getCurrentTourStep() === 3 && (link.path === '/therapy' || link.path === '/sanctuary')) ? 'fill-current' : ''}`}
            style={(getCurrentTourStep() === 3 && (link.path === '/therapy' || link.path === '/sanctuary')) ? { color: '#CBB281', fill: '#CBB281' } : undefined}
          />
        </button>
      </div>
    );
  };

  // Category definitions for the 6 domains
  const categories = [
    { key: 'home', label: '🏠 HOME', bg: 'bg-accent/30 hover:bg-accent/50 text-foreground' },
    { key: 'therappi-ai', label: '🧠 THERAPPI AI', bg: 'bg-primary/15 hover:bg-primary/25 text-foreground' },
    { key: 'track', label: '📊 TRACK', bg: 'bg-light-teal/40 hover:bg-light-teal/60 text-foreground' },
    { key: 'sanctuary', label: '🌿 SANCTUARY', bg: 'bg-secondary/30 hover:bg-secondary/50 text-foreground' },
    { key: 'grow', label: '✨ GROW', bg: 'bg-muted/50 hover:bg-muted text-foreground' },
    { key: 'connect', label: '👥 CONNECT', bg: 'bg-accent/20 hover:bg-accent/40 text-foreground' },
  ];

  return (
    <>
      <MobileNavIntro isOpen={open} onDismiss={() => {}} />
      <Sheet open={open} onOpenChange={(isOpen) => {
          // Prevent closing the sheet during tour
          if (isTourRunning && !isOpen) return;
          setOpen(isOpen);
        }}>
        <SheetTrigger asChild>
          <Button 
            variant="ghost" 
            size="icon" 
            className="md:hidden hover:bg-secondary/20 h-16 w-16"
            data-tour="menu-button"
            style={getCurrentTourStep() === 2 ? { transform: 'translateX(11px)' } : undefined}
          >
            <Menu className="h-10 w-10" style={{ color: '#CBB281' }} strokeWidth={2.5} />
          </Button>
        </SheetTrigger>
        <SheetContent 
          side="left" 
          className="w-80 overflow-y-auto z-[250]"
        >
          {/* Tour steps 4-12: wrapper that physically moves menu content UP so target items are visible above the tour card */}
          <div
            style={(() => {
              const step = getCurrentTourStep();
              // Step-specific translateY offsets to ensure highlighted menu items are visible
              const tourMenuOffsets: Record<number, number> = {
                3: -80,   // Step 4: Favourites (Therappi AI star)
                4: -120,  // Step 5: Therappi AI row
                5: -160,  // Step 6: Daily Check-In
                6: -200,  // Step 7: Journal
                7: -240,  // Step 8: Mood Tracking
                8: -280,  // Step 9: Sleep Tracker
                9: -320,  // Step 10: Goals
                10: -360, // Step 11: Resources
                11: -400, // Step 12: Settings
              };
              const offset = tourMenuOffsets[step];
              return offset !== undefined 
                ? { transform: `translateY(${offset}px)`, transition: 'transform 0.2s ease' } 
                : undefined;
            })()}
          >
          <SheetHeader>
            <SheetTitle className="text-left">Navigation</SheetTitle>
          </SheetHeader>

        {/* User Info */}
        <div className="mt-6 mb-4 p-4 bg-muted rounded-lg">
          <p className="text-sm font-medium truncate" style={{ color: 'hsl(182 29% 20%)' }}>{user?.email}</p>
          <div className="flex flex-wrap gap-2 mt-2">
            {isAdmin && (
              <Badge variant="default" className="text-xs">
                <Crown className="h-3 w-3 mr-1" />
                Admin
              </Badge>
            )}
            {isClinician && (
              <Badge variant="default" className="text-xs bg-emerald-600">
                <Stethoscope className="h-3 w-3 mr-1" />
                Clinician
              </Badge>
            )}
            {isPremium && !isAdmin && (
              <Badge variant="secondary" className="text-xs">
                Premium
              </Badge>
            )}
            {hasClinicianConnection && (
              <Badge variant="outline" className="text-xs">
                <UserCircle className="h-3 w-3 mr-1" />
                Connected
              </Badge>
            )}
          </div>
        </div>

        {/* Favourites Section */}
        <div className="mt-6" data-tour="favorites-section">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-medium text-muted-foreground tracking-wide">
              YOUR FAVOURITES
            </p>
            <button
              onClick={() => toast.info('Tap the star icon on any menu item to add it to favorites')}
              className="text-xs text-primary hover:text-primary-light flex items-center gap-1"
            >
              <Plus className="h-3 w-3" />
              Add
            </button>
          </div>
          <nav className="flex flex-col gap-1.5">
            {displayFavorites.map((link) => renderNavLink(link, 'bg-muted/60 hover:bg-muted text-foreground'))}
          </nav>
        </div>

        {/* Domain Categories */}
        {categories.map(cat => {
          const items = allNavLinks.filter(l => l.category === cat.key && !displayFavorites.some(f => f.path === l.path));
          if (items.length === 0) return null;
          return (
            <div key={cat.key} className="mt-6">
              <p className="text-xs font-medium text-muted-foreground mb-3 tracking-wide">
                {cat.label}
              </p>
              <nav className="flex flex-col gap-1.5">
                {items.map((link) => renderNavLink(link, cat.bg))}
              </nav>
            </div>
          );
        })}

        {/* Crisis Support - always visible */}
        {(() => {
          const crisisLink = allNavLinks.find(l => l.path === '/crisis');
          if (!crisisLink || displayFavorites.some(f => f.path === '/crisis')) return null;
          return (
            <div className="mt-6">
              <p className="text-xs font-medium text-muted-foreground mb-3 tracking-wide">
                🛟 SUPPORT
              </p>
              <nav className="flex flex-col gap-1.5">
                {renderNavLink(crisisLink, 'bg-destructive/10 hover:bg-destructive/20 text-foreground')}
              </nav>
            </div>
          );
        })()}

        {/* Account */}
        {(() => {
          const accountItems = allNavLinks.filter(l => l.category === 'account' && !displayFavorites.some(f => f.path === l.path));
          if (accountItems.length === 0) return null;
          return (
            <div className="mt-6">
              <p className="text-xs font-medium text-muted-foreground mb-3 tracking-wide">
                ⚙️ ACCOUNT
              </p>
              <nav className="flex flex-col gap-1.5">
                {accountItems.map((link) => renderNavLink(link, 'bg-muted/30 hover:bg-muted/50 text-foreground'))}
              </nav>
            </div>
          );
        })()}

        {/* Clinician Portal */}
        {isClinician && (
          <div className="mt-6 pt-6 border-t">
            <p className="text-xs font-medium text-muted-foreground mb-3 tracking-wide">
              CLINICIAN
            </p>
            <Link
              to="/clinician"
              onClick={(e) => {
                if (isTourRunning) {
                  e.preventDefault();
                  return;
                }
                setOpen(false);
              }}
              className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${
                isActive('/clinician')
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-emerald-500/20 hover:bg-emerald-500/30 text-foreground'
              }`}
            >
              <Stethoscope className="h-5 w-5" />
              <span className="font-medium">Clinician Portal</span>
            </Link>
          </div>
        )}

        {/* Client Portal (for users connected to clinicians) */}
        {hasClinicianConnection && (
          <div className="mt-6 pt-6 border-t">
            <p className="text-xs font-medium text-muted-foreground mb-3 tracking-wide">
              MY THERAPY
            </p>
            <Link
              to="/client-portal"
              onClick={(e) => {
                if (isTourRunning) {
                  e.preventDefault();
                  return;
                }
                setOpen(false);
              }}
              className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${
                isActive('/client-portal')
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted/50 hover:bg-muted text-foreground'
              }`}
            >
              <UserCircle className="h-5 w-5" />
              <span className="font-medium">My Clinician</span>
            </Link>
          </div>
        )}

        {/* Admin Section */}
        {isAdmin && (
          <div className="mt-6 pt-6 border-t">
            <p className="text-xs font-medium text-muted-foreground mb-3 tracking-wide">
              ADMIN TOOLS
            </p>
            <Link
              to="/admin"
              onClick={(e) => {
                if (isTourRunning) {
                  e.preventDefault();
                  return;
                }
                setOpen(false);
              }}
              className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${
                isActive('/admin')
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-red-500/20 hover:bg-red-500/30 text-foreground'
              }`}
            >
              <Settings className="h-5 w-5" />
              <span className="font-medium">Admin Portal</span>
            </Link>
            <Link
              to="/business"
              onClick={(e) => {
                if (isTourRunning) {
                  e.preventDefault();
                  return;
                }
                setOpen(false);
              }}
              className={`flex items-center gap-3 p-3 rounded-lg transition-colors mt-1.5 ${
                isActive('/business')
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-red-500/20 hover:bg-red-500/30 text-foreground'
              }`}
            >
              <Building2 className="h-5 w-5" />
              <span className="font-medium">Business Operations</span>
            </Link>
          </div>
        )}

        {/* Bottom Actions */}
        <div className="mt-6 pt-6 border-t space-y-2">
          <Button
            variant="outline"
            className="w-full justify-start"
            data-tour="settings-button"
            onClick={() => {
              // Prevent navigation during tour
              if (isTourRunning) return;
              setOpen(false);
              navigate('/settings');
            }}
          >
            <Bell className="h-4 w-4 mr-2" />
            Notifications & Settings
          </Button>
          
          <Button
            variant="ghost"
            className="w-full justify-start text-muted-foreground hover:text-foreground"
            onClick={() => {
              // Clear tour seen flag and trigger tour restart
              if (isTourRunning) return;
              console.log('[MobileNav] Revisit Tour clicked - clearing flags and starting tour');
              localStorage.removeItem('therappi-first-time-tour-seen');
              localStorage.removeItem('therappi_welcome_seen_/');
              // CRITICAL: Prepare tour FIRST (before closing menu) to set flags
              tourController.prepareTour();
              setOpen(false);
              // Longer delay to ensure menu is fully closed and component is ready
              setTimeout(() => {
                console.log('[MobileNav] Firing startTour after delay');
                tourController.startTour();
              }, 500);
            }}
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Revisit App Tour
          </Button>
          
          <Button
            variant="default"
            className="w-full bg-primary text-primary-foreground hover:bg-primary-light"
            onClick={() => {
              // Prevent sign out during tour
              if (isTourRunning) return;
              onSignOut();
              setOpen(false);
            }}
          >
            <LogOut className="h-4 w-4 mr-2" />
            Sign Out
          </Button>
        </div>
          </div>
      </SheetContent>
    </Sheet>
    </>
  );
};
